import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { listDistribution } from '../../../../services';
import { validateApiResponse } from '../../../../shared/utils/apiResponse';
import { STATE_TO_BUCKET, SURFACE_KEYS } from '../../constants';

// The surfaces an integration can actually be exposed to, N/A cells excluded.
// The summary tiles count over this same surface model the table renders — not
// studios or workspace-mode — so the numbers agree with the grid.
const applicableSurfaces = (row) => SURFACE_KEYS.map((key) => row.surfaces?.[key]).filter((v) => v != null);

/**
 * Distribution data (CT-4206): all connected integrations with their studio +
 * workspace exposure. Search by name/vendor/owner; status filters the health
 * bucket. `reload()` reflects panel edits (CT-4207/4208).
 */
export function useDistribution(search = '', status = 'all') {
  const { t } = useTranslation('admin-studio');
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  const fetchIdRef = useRef(0);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    const fetchId = ++fetchIdRef.current;
    setIsLoading(true);
    setError(null);
    try {
      const res = await listDistribution();
      if (!mountedRef.current || fetchId !== fetchIdRef.current) return;
      setRows(validateApiResponse(res, t('common.requestFailed')).body ?? []);
    } catch (err) {
      if (!mountedRef.current || fetchId !== fetchIdRef.current) return;
      setError(err?.message ?? t('common.requestFailed'));
    } finally {
      if (mountedRef.current && fetchId === fetchIdRef.current) setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load, reloadToken]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (q && !`${r.name} ${r.vendor ?? ''} ${r.owner ?? ''}`.toLowerCase().includes(q)) return false;
      if (status !== 'all' && STATE_TO_BUCKET[r.status] !== status) return false;
      return true;
    });
  }, [rows, search, status]);

  const stats = useMemo(
    () => ({
      connected: rows.length,
      exposedAll: rows.filter((r) => {
        const surfaces = applicableSurfaces(r);
        return surfaces.length > 0 && surfaces.every(Boolean);
      }).length,
      unexposed: rows.filter((r) => {
        const surfaces = applicableSurfaces(r);
        return surfaces.length === 0 || surfaces.every((v) => !v);
      }).length,
    }),
    [rows],
  );

  const reload = useCallback(() => setReloadToken((n) => n + 1), []);

  return { rows: filtered, stats, isLoading, error, reload };
}
